import React, { useEffect, useState, useMemo } from 'react';
import { tokens, type } from '../theme';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { COMPETITION_LABELS, QOP_AGE_GROUPS } from '../config/seasons';
import FixtureCard from '../components/FixtureCard';
import useGameWeather from '../hooks/useGameWeather';
import { TAP_TARGET } from '../utils/tapTarget';
import { getTeamDetail, getGames, getStandings } from '../services/api';

import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Avatar,
  IconButton,
  Card,
  CardContent
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const TeamDetail = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Data state
  const [teamInfo, setTeamInfo] = useState(null);
  const [games, setGames] = useState([]);
  const weather = useGameWeather(games);
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Step 1: Load team info
  useEffect(() => {
    const loadTeamInfo = async () => {
      setLoading(true);
      setError(null);
      try {
        const info = await getTeamDetail(teamId);
        setTeamInfo(info);
      } catch (err) {
        console.error('Error loading team info:', err);
        setError('Team not found');
        setLoading(false);
      }
    };
    loadTeamInfo();
  }, [teamId]);

  // Step 2: Once teamInfo loaded, fetch games + standings in parallel
  useEffect(() => {
    if (!teamInfo) return;

    const loadData = async () => {
      try {
        const [gamesData, standingsData] = await Promise.all([
          getGames(
            [teamInfo.age_group],
            null,
            teamInfo.division_id,
            teamInfo.season,
            [parseInt(teamId)],
            0,
            500,
            'all'
          ),
          teamInfo.conference_id
            ? getStandings(
                teamInfo.age_group,
                teamInfo.conference_id,
                teamInfo.division_id,
                teamInfo.season
              )
            : Promise.resolve([])
        ]);

        setGames(gamesData.games || []);
        setStandings(Array.isArray(standingsData) ? standingsData : []);
      } catch (err) {
        console.error('Error loading team data:', err);
        setError('Error loading team data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [teamInfo, teamId]);

  // Extract this team's stats from standings
  const teamStats = useMemo(() => {
    if (!standings.length || !teamInfo) return null;
    return standings.find(s => s.team_id === parseInt(teamId)) || null;
  }, [standings, teamId, teamInfo]);

  // Split games into results and upcoming
  const { results, upcoming } = useMemo(() => {
    const played = [];
    const future = [];
    games.forEach(game => {
      if (game.home_score === 'TBD' || game.home_score === null || game.home_score === undefined) {
        future.push(game);
      } else {
        played.push(game);
      }
    });
    // Results: most recent first
    played.sort((a, b) => new Date(b.date) - new Date(a.date));
    // Upcoming: soonest first
    future.sort((a, b) => new Date(a.date) - new Date(b.date));
    return { results: played, upcoming: future };
  }, [games]);

  // Build head-to-head records from games data
  const h2hRecords = useMemo(() => {
    if (!games.length) return [];
    const tid = parseInt(teamId);
    const opponents = {};

    games.forEach(game => {
      // Skip games without scores
      if (game.home_score === 'TBD' || game.home_score === null || game.home_score === undefined) return;

      const isHome = game.home_team_id === tid;
      const oppId = isHome ? game.away_team_id : game.home_team_id;
      const oppName = isHome ? game.away_team : game.home_team;
      const oppLogo = isHome ? game.away_logo : game.home_logo;
      const gf = isHome ? game.home_score : game.away_score;
      const ga = isHome ? game.away_score : game.home_score;

      if (!opponents[oppId]) {
        opponents[oppId] = { id: oppId, name: oppName, logo: oppLogo, GP: 0, W: 0, L: 0, D: 0, GF: 0, GA: 0 };
      }
      const opp = opponents[oppId];
      opp.GP++;
      opp.GF += gf;
      opp.GA += ga;
      if (gf > ga) opp.W++;
      else if (gf < ga) opp.L++;
      else opp.D++;
    });

    return Object.values(opponents).sort((a, b) => b.GP - a.GP);
  }, [games, teamId]);

  // Helper: format game score
  const formatScore = (game) => {
    if (game.home_score === 'TBD' || game.home_score === null || game.home_score === undefined) {
      return 'TBD';
    }
    return `${game.home_score} : ${game.away_score}`;
  };

  // Helper: get result for this team in a game
  const getResult = (game) => {
    const tid = parseInt(teamId);
    const isHome = game.home_team_id === tid;
    const gf = isHome ? game.home_score : game.away_score;
    const ga = isHome ? game.away_score : game.home_score;
    if (gf > ga) return 'W';
    if (gf < ga) return 'L';
    return 'D';
  };

  const resultColor = (r) => {
    if (r === 'W') return tokens.win;
    if (r === 'L') return tokens.loss;
    return tokens.inkFaint;
  };

  // Render a game card (reuses Schedule.js visual pattern)
  const renderGameCard = (game) => {
    const tid = parseInt(teamId);
    const isHome = game.home_team_id === tid;
    const isAway = game.away_team_id === tid;
    const gameDate = new Date(game.date);
    const hasScore = game.home_score !== 'TBD' && game.home_score !== null && game.home_score !== undefined;
    const result = hasScore ? getResult(game) : null;

    return (
      <FixtureCard
        key={game.match_id}
        game={game}
        isMobile={isMobile}
        accentColor={result ? resultColor(result) : null}
        highlightHome={isHome}
        highlightAway={isAway}
        linkHome={!isHome}
        linkAway={!isAway}
        weather={weather[game.match_id] || null}
      />
    );
  };

  // Stat card component
  const StatCard = ({ label, value, color }) => (
    <Paper
      sx={{
        p: isMobile ? 1.5 : 2,
        textAlign: 'center',
        borderRadius: 0,
        border: '1px solid #e9ecef'
      }}
      elevation={0}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: color || tokens.ink,
          fontSize: isMobile ? type.lead : type.title,
          lineHeight: 1
        }}
      >
        {value}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: tokens.inkMuted,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          fontSize: type.label
        }}
      >
        {label}
      </Typography>
    </Paper>
  );

  // Error state
  if (error && !teamInfo) {
    return (
      <Box sx={{ width: '100%', backgroundColor: '#f8f9fa', minHeight: '100vh', py: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" color="error">{error}</Typography>
        </Box>
      </Box>
    );
  }

  // Loading state
  if (loading || !teamInfo) {
    return (
      <Box sx={{ width: '100%', backgroundColor: '#f8f9fa', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const isHomegrown = teamInfo.division_name === 'Homegrown Division';

  return (
    <Box sx={{ width: '100%', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>

      {/* Team Header */}
      {/* Gutter as margin, not padding. Carrying it as padding left the white
          band and its bottom border running to the column edge while every card
          below sat 24px inside — the same mismatch the filter bar and the
          next-match card each had. */}
      <Box sx={{
        mx: isMobile ? 1.5 : 3,
        px: isMobile ? 1.5 : 2,
        py: isMobile ? 2 : 3,
        backgroundColor: 'white',
        borderBottom: '2px solid #e9ecef'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate(-1)} sx={{ mr: 1, color: tokens.inkMuted }}>
            <ArrowBackIcon />
          </IconButton>
          {teamInfo.logo_url && (
            <Avatar
              src={teamInfo.logo_url}
              alt={teamInfo.team_name}
              sx={{
                width: isMobile ? 48 : 64,
                height: isMobile ? 48 : 64,
                mr: 2
              }}
            />
          )}
          <Box>
            <Typography
              variant={isMobile ? 'h6' : 'h5'}
              sx={{ fontWeight: 700, color: tokens.ink, lineHeight: 1.2 }}
            >
              {teamInfo.team_name}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: tokens.inkMuted, fontSize: isMobile ? type.caption : type.bodySm }}
            >
              {[teamInfo.division_name, teamInfo.conference_name, teamInfo.age_group, teamInfo.season]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
            {teamInfo.prev_season && (
              <Typography
                variant="body2"
                sx={{ fontSize: isMobile ? type.caption : type.bodySm, mt: 0.25 }}
              >
                <Link
                  to={`/team/${teamInfo.prev_season.team_id}`}
                  style={{
                    color: tokens.navy,
                    textDecoration: 'underline',
                    // Block hit area so the target clears 44px without changing the look.
                    display: 'inline-flex',
                    alignItems: 'center',
                    minHeight: 44,
                    paddingRight: 8
                  }}
                >
                  {`${teamInfo.prev_season.season} season (as ${teamInfo.prev_season.age_group})`}
                </Link>
              </Typography>
            )}
            {teamStats && (
              <Typography
                variant="body2"
                sx={{ color: tokens.navy, fontWeight: 600, fontSize: isMobile ? type.caption : type.bodySm, mt: 0.25 }}
              >
                {teamStats.W}W - {teamStats.L}L - {teamStats.D}D · {teamStats.PTS} PTS
                {teamStats.Rk ? ` · Rank #${teamStats.Rk}` : ''}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Season Summary Stats */}
      {teamStats && (
        <Box sx={{ px: isMobile ? 1.5 : 3, py: 2 }}>
          <Grid container spacing={1}>
            <Grid size={{ xs: 4, sm: 3, md: 1.5 }}>
              <StatCard label="Record" value={`${teamStats.W}-${teamStats.L}-${teamStats.D}`} />
            </Grid>
            <Grid size={{ xs: 4, sm: 3, md: 1.5 }}>
              <StatCard label="Points" value={teamStats.PTS} color="#0D203D" />
            </Grid>
            <Grid size={{ xs: 4, sm: 3, md: 1.5 }}>
              <StatCard label="GF" value={teamStats.GF} />
            </Grid>
            <Grid size={{ xs: 4, sm: 3, md: 1.5 }}>
              <StatCard label="GA" value={teamStats.GA} />
            </Grid>
            <Grid size={{ xs: 4, sm: 3, md: 1.5 }}>
              <StatCard
                label="GD"
                value={teamStats.GD > 0 ? `+${teamStats.GD}` : teamStats.GD}
                color={teamStats.GD > 0 ? tokens.win : teamStats.GD < 0 ? tokens.loss : tokens.inkMuted}
              />
            </Grid>
            <Grid size={{ xs: 4, sm: 3, md: 1.5 }}>
              <StatCard label="GF/M" value={teamStats['GF/M']?.toFixed(1) || '0.0'} />
            </Grid>
            <Grid size={{ xs: 4, sm: 3, md: 1.5 }}>
              <StatCard label="GA/M" value={teamStats['GA/M']?.toFixed(1) || '0.0'} />
            </Grid>
            <Grid size={{ xs: 4, sm: 3, md: 1.5 }}>
              <StatCard label="Clean Sheets" value={teamStats.CS} />
            </Grid>
            {/* QoP exists only for U13/U14 — gate on age explicitly rather than
                relying on the value happening to be null above that. */}
            {isHomegrown && QOP_AGE_GROUPS.includes(teamInfo.age_group)
              && teamStats.qop_score != null && (
              <>
                <Grid size={{ xs: 4, sm: 3, md: 1.5 }}>
                  <StatCard label="QoP" value={teamStats.qop_score?.toFixed(1)} color={tokens.navy} />
                </Grid>
                {teamStats.att_score != null && (
                  <Grid size={{ xs: 4, sm: 3, md: 1.5 }}>
                    <StatCard label="ATT" value={teamStats.att_score?.toFixed(1)} />
                  </Grid>
                )}
                {teamStats.def_score != null && (
                  <Grid size={{ xs: 4, sm: 3, md: 1.5 }}>
                    <StatCard label="DEF" value={teamStats.def_score?.toFixed(1)} />
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </Box>
      )}

      {/* Results */}
      {results.length > 0 && (
        <Box sx={{ px: isMobile ? 1.5 : 3, pb: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: tokens.inkMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontSize: type.label,
              mb: 1.5
            }}
          >
            Results ({results.length})
          </Typography>
          {results.map(game => renderGameCard(game))}
        </Box>
      )}

      {/* Upcoming */}
      {upcoming.length > 0 && (
        <Box sx={{ px: isMobile ? 1.5 : 3, pb: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: tokens.inkMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontSize: type.label,
              mb: 1.5
            }}
          >
            Upcoming ({upcoming.length})
          </Typography>
          {upcoming.map(game => renderGameCard(game))}
        </Box>
      )}

      {/* Head-to-Head Records */}
      {h2hRecords.length > 0 && (
        <Box sx={{ px: isMobile ? 1.5 : 3, pb: 3 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: tokens.inkMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontSize: type.label,
              mb: 1.5
            }}
          >
            Head-to-Head
          </Typography>
          <Paper sx={{ overflow: 'hidden', borderRadius: 0 }}>
            <Box sx={{ overflowX: 'auto' }}>
              <Table
                sx={{
                  minWidth: isMobile ? 450 : '100%',
                  '& th, & td': {
                    padding: isMobile ? '8px 6px' : '10px 12px',
                    borderBottom: '1px solid #f1f3f4',
                  },
                  '& th': {
                    backgroundColor: '#fafafa',
                    fontWeight: 600,
                    color: tokens.inkMuted,
                    fontSize: type.label,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  },
                  '& td': {
                    fontSize: isMobile ? type.caption : type.bodySm,
                    color: tokens.ink
                  }
                }}
                size="small"
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ textAlign: 'left' }}>Opponent</TableCell>
                    <TableCell sx={{ textAlign: 'center', width: 45 }}>GP</TableCell>
                    <TableCell sx={{ textAlign: 'center', width: 45 }}>W</TableCell>
                    <TableCell sx={{ textAlign: 'center', width: 45 }}>L</TableCell>
                    <TableCell sx={{ textAlign: 'center', width: 45 }}>D</TableCell>
                    <TableCell sx={{ textAlign: 'center', width: 45 }}>GF</TableCell>
                    <TableCell sx={{ textAlign: 'center', width: 45 }}>GA</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {h2hRecords.map(opp => (
                    <TableRow
                      key={opp.id}
                      sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {opp.logo && (
                            <Avatar
                              src={opp.logo}
                              alt={opp.name}
                              sx={{ width: 20, height: 20 }}
                              variant="square"
                            />
                          )}
                          <Typography
                            variant="body2"
                            component={Link}
                            to={`/team/${opp.id}`}
                            sx={{
                              fontWeight: 500,
                              color: tokens.ink,
                              textDecoration: 'none',
                              fontSize: isMobile ? type.caption : type.bodySm,
                              ...TAP_TARGET,
                              '&:hover': { textDecoration: 'underline' }
                            }}
                          >
                            {opp.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{opp.GP}</TableCell>
                      <TableCell sx={{ textAlign: 'center', fontWeight: opp.W > 0 ? 600 : 400, color: opp.W > 0 ? tokens.win : 'inherit' }}>
                        {opp.W}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center', fontWeight: opp.L > 0 ? 600 : 400, color: opp.L > 0 ? tokens.loss : 'inherit' }}>
                        {opp.L}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{opp.D}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{opp.GF}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{opp.GA}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Empty state: no games at all */}
      {games.length === 0 && !loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <Typography variant="body1" color="textSecondary">
            No games found for this team.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default TeamDetail;
